import type { RequestOpts } from '@oazapfts/runtime';
import { Err } from 'ts-results-es';

import { appConfig } from '@/config';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';

import { FeApi } from '..';
import type { ICapacityEvaluationRepository } from '../domain/repositories/capacity-evaluation.repository';
import type { RequestOptions } from '../domain/repositories/utils/request-options';
import { handle } from '../helpers/handle';
import { CapacityEvaluationRepositoryMapper } from '../mappers/capacity-evaluation.repository.mapper';

export class FeApiCapacityEvaluationRepository implements ICapacityEvaluationRepository {
  constructor(
    private me: Me,
    private auth: Auth,
  ) {}

  private async getOpts(): Promise<RequestOpts> {
    const accessToken = await this.auth.getAccessToken();

    return {
      baseUrl: `${appConfig.bffUrl}/plex`,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-user-email': this.me.email,
        'x-user-name': this.me.displayName,
      },
    };
  }

  async getAll(
    input: ICapacityEvaluationRepository.GetAllInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.GetAllOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1JobListRetrieve(
      CapacityEvaluationRepositoryMapper.getAll.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ICapacityEvaluationRepository.GetAllOutput>({
      async 200(data) {
        return CapacityEvaluationRepositoryMapper.getAll.outputFromData(data);
      },
    });
  }

  async getById(
    input: ICapacityEvaluationRepository.GetByIdInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.GetByIdOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1JobDetailsRetrieve(
      CapacityEvaluationRepositoryMapper.getById.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ICapacityEvaluationRepository.GetByIdOutput>({
      async 200(data) {
        return CapacityEvaluationRepositoryMapper.getById.outputFromData(data);
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }

  async getLevels(
    input: ICapacityEvaluationRepository.GetLevelsInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.GetLevelsOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1JobLevelsRetrieve(
      CapacityEvaluationRepositoryMapper.getLevels.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ICapacityEvaluationRepository.GetLevelsOutput>({
      async 200(data) {
        return CapacityEvaluationRepositoryMapper.getLevels.outputFromData(
          data,
        );
      },
    });
  }

  async getAggregatedResults(
    input: ICapacityEvaluationRepository.GetAggregatedResultsInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.GetAggregatedResultsOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1JobResultsList(
      CapacityEvaluationRepositoryMapper.getAggregatedResults.bodyFromInput(
        input,
      ),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(
      response,
    ).to<ICapacityEvaluationRepository.GetAggregatedResultsOutput>({
      async 200(data) {
        return CapacityEvaluationRepositoryMapper.getAggregatedResults.outputFromData(
          data,
        );
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }

  async create(
    input: ICapacityEvaluationRepository.CreateInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.CreateOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1JobNewCreate(
      CapacityEvaluationRepositoryMapper.create.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ICapacityEvaluationRepository.CreateOutput>({
      async 201(data) {
        return CapacityEvaluationRepositoryMapper.create.outputFromData(data);
      },
      async 400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }

  async getSohData(
    input: ICapacityEvaluationRepository.GetSohDataInput,
    requestOptions?: RequestOptions,
  ): Promise<ICapacityEvaluationRepository.GetSohDataOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.capacityEvaluationV1SiteSohList(
      CapacityEvaluationRepositoryMapper.getSohData.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ICapacityEvaluationRepository.GetSohDataOutput>({
      async 200(data) {
        return CapacityEvaluationRepositoryMapper.getSohData.outputFromData(
          data,
        );
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }
}
